import type { ReactNode } from 'react';

import { classes } from '../lib/divers';
import { SqueletteTableau } from './Chargement';
import { EtatVide } from './EtatVide';

export interface Colonne<T> {
  /** Clé technique de la colonne (identité React, pas un libellé). */
  cle: string;
  entete: ReactNode;
  rendu: (ligne: T) => ReactNode;
  /** Aligne à droite et applique les chiffres tabulaires. */
  numerique?: boolean;
  largeur?: string;
}

export interface SelectionTableau {
  selectionnes: ReadonlySet<string>;
  basculer: (cle: string) => void;
  /** Coche ou décoche toutes les lignes visibles. */
  basculerTout: (cles: readonly string[]) => void;
}

interface ProprietesTableau<T> {
  colonnes: ReadonlyArray<Colonne<T>>;
  lignes: readonly T[];
  cleDe: (ligne: T) => string;
  chargement?: boolean;
  titreVide?: string;
  descriptionVide?: string;
  actionVide?: ReactNode;
  surClic?: (ligne: T) => void;
  /** Clé de la ligne mise en évidence. */
  ligneActive?: string | null;
  selection?: SelectionTableau;
  pied?: ReactNode;
}

/**
 * Tableau de données.
 *
 * Le défilement horizontal est confié au conteneur (`overflow-x`) : sur un écran de portable,
 * un tableau de diagnostic à onze colonnes doit défiler dans son cadre, jamais faire défiler la
 * page entière — sinon la barre latérale et l'en-tête partent avec.
 */
export function Tableau<T>({
  colonnes,
  lignes,
  cleDe,
  chargement = false,
  titreVide = 'Aucun résultat',
  descriptionVide,
  actionVide,
  surClic,
  ligneActive = null,
  selection,
  pied,
}: ProprietesTableau<T>): JSX.Element {
  if (chargement && lignes.length === 0) {
    return <SqueletteTableau colonnes={colonnes.length} />;
  }

  if (lignes.length === 0) {
    return (
      <EtatVide
        titre={titreVide}
        {...(descriptionVide ? { description: descriptionVide } : {})}
        {...(actionVide ? { action: actionVide } : {})}
      />
    );
  }

  const cles = lignes.map(cleDe);
  const toutesSelectionnees =
    selection !== undefined && cles.length > 0 && cles.every((c) => selection.selectionnes.has(c));

  return (
    <>
      <div className="gv-table-cadre">
        <table className="gv-table">
          <thead>
            <tr>
              {selection ? (
                <th style={{ width: 44 }}>
                  <input
                    type="checkbox"
                    checked={toutesSelectionnees}
                    aria-label="Tout sélectionner"
                    onChange={() => {
                      selection.basculerTout(cles);
                    }}
                  />
                </th>
              ) : null}
              {colonnes.map((c) => (
                <th
                  key={c.cle}
                  className={classes(c.numerique && 'gv-table__num')}
                  style={c.largeur ? { width: c.largeur } : undefined}
                >
                  {c.entete}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lignes.map((ligne) => {
              const cle = cleDe(ligne);
              const selectionnee = selection?.selectionnes.has(cle) ?? false;
              return (
                <tr
                  key={cle}
                  className={classes(
                    surClic && 'gv-table__ligne--cliquable',
                    (selectionnee || cle === ligneActive) && 'gv-table__ligne--selectionnee',
                  )}
                  onClick={surClic ? () => { surClic(ligne); } : undefined}
                  // Une ligne cliquable doit l'être au clavier : c'est le seul moyen d'ouvrir
                  // une fiche appareil sans souris.
                  tabIndex={surClic ? 0 : undefined}
                  onKeyDown={
                    surClic
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            surClic(ligne);
                          }
                        }
                      : undefined
                  }
                >
                  {selection ? (
                    <td
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectionnee}
                        aria-label={`Sélectionner ${cle}`}
                        onChange={() => {
                          selection.basculer(cle);
                        }}
                      />
                    </td>
                  ) : null}
                  {colonnes.map((c) => (
                    <td key={c.cle} className={classes(c.numerique && 'gv-table__num')}>
                      {c.rendu(ligne)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {pied ? <div className="gv-table__pied">{pied}</div> : null}
    </>
  );
}
